import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import {
  Avatar,
  Button,
  CircularLoader,
  DocumentationIcon,
  DownArrowIcon,
  DropdownMenu,
  GlobeIcon,
  InfoIcon,
  Input,
  KbFileIcon,
  SelectField,
  TableChip,
} from "components";
import {
  useGlobalStore,
  useMoveKbRecords,
  usePermissionStore,
} from "controller";
import React, { useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { KbFileDetailResponse, KbRecordItem, PERMISSIONS } from "shared";
import {
  claculateTimeElapsed,
  getDateWithMonthText,
  getFormattedDate,
  getFullName,
} from "utilz/helpers";
import { KbFileListDrawer } from "./KbFileListDrawer";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { KbFormBottomButtons } from "./KbFormBottomButtons";
import { ActionButtonType } from "../types";
import { useAtomValue } from "jotai";
import { kbFolderTreeArr } from "../store";
import { KbRecordVisibilityChip } from "./KbRecordVisibilityChip";
import { KbShareLink } from "./KbShareLink";
import toast from "react-hot-toast";

export const KbDetailsForm = ({
  kbFileDetails,
  isFileDetailsLoading = false,
  onActionButtonClick,
  isPrimaryActionLoading = false,
  isSecondaryActionLoading = false,
  mode,
  tabValue = 0,
  snapshot,
}: {
  kbFileDetails: KbFileDetailResponse;
  isFileDetailsLoading: boolean;
  onActionButtonClick: (type: ActionButtonType) => void;
  mode: "edit" | "view";
  isPrimaryActionLoading: boolean;
  isSecondaryActionLoading: boolean;
  tabValue?: number;
  snapshot?: KbFileDetailResponse;
}) => {
  const theme = useTheme();

  const haveFullAccess = usePermissionStore((state) =>
    state.hasPermissions([PERMISSIONS.KB.KB_KB_RW]),
  );
  const kbFolderTreeAtomData = useAtomValue(kbFolderTreeArr);
  const { control, setValue, watch } = useFormContext();
  const [open, setOpen] = React.useState(false);
  const [parentName, setParentName] = React.useState<string>(
    kbFileDetails?.parent_name,
  );
  const dateFormat = useGlobalStore((state) => state.dateFormat);
  const timezone = useGlobalStore((state) => state.timezone);
  const { mutateAsync: moveRecord } = useMoveKbRecords();
  const fullName = getFullName(
    kbFileDetails?.owner_first_name,
    "",
    kbFileDetails?.owner_last_name,
  );

  const updatedByName = useMemo(
    () =>
      getFullName(
        kbFileDetails?.updated_by_first_name,
        "",
        kbFileDetails?.updated_by_last_name,
      ),
    [kbFileDetails],
  );
  const hasUnsavedVersion = snapshot && !snapshot.is_published;

  useEffect(() => {
    if (kbFileDetails) {
      setParentName(kbFileDetails?.parent_name);
    }
  }, [kbFileDetails]);

  // Function to recursively search for folder by ID
  const findFolderById = (
    items: typeof kbFolderTreeAtomData,
    targetId: string,
  ): KbRecordItem | null => {
    for (const item of items) {
      if (item.mapping_id === targetId) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = findFolderById(item.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const formattedDate = getDateWithMonthText({
    date: kbFileDetails?.created_at,
    format: dateFormat,
    time: true,
    timezone,
  });

  const hasPublishedVersions =
    kbFileDetails?.versions && kbFileDetails.versions.length > 1;

  const draftVersion = useMemo(() => {
    return (
      kbFileDetails?.versions?.find(
        (version) =>
          version.record_visibility === "draft" && version.version === 0,
      ) || {}
    );
  }, [kbFileDetails]);

  const handleMoveRecord = async (folderId: string) => {
    try {
      await moveRecord({
        record_ids: [kbFileDetails.mapping_id],
        parent_id: folderId,
      });
      toast.success("Article moved successfully");
    } catch (error) {
      toast.error("Unable to move article");
    }
  };

  const liveVersion = useMemo(() => {
    return kbFileDetails?.versions?.find((ver) => ver.is_live);
  }, [kbFileDetails?.versions]);
  return (
    <>
      {isFileDetailsLoading ? (
        <Stack
          sx={{
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularLoader />
        </Stack>
      ) : (
        <>
          {tabValue === 0 || mode === "edit" ? (
            <Stack sx={{ p: "12px 24px", gap: "16px" }} divider={<Divider />}>
              <Stack sx={{ gap: "16px" }}>
                <Typography variant="subtitle1">Article details</Typography>

                <Stack direction={"row"} alignItems={"center"} gap="16px">
                  <Typography
                    variant="body-small"
                    color={theme.palette.lightBg.low}
                    width={"90px"}
                  >
                    Type
                  </Typography>
                  <KbRecordVisibilityChip
                    visibility={liveVersion?.record_visibility || "unsaved"}
                  />
                </Stack>

                <Stack direction={"row"} alignItems={"center"} gap="16px">
                  <Typography
                    variant="body-small"
                    color={theme.palette.lightBg.low}
                    width={"90px"}
                  >
                    Source
                  </Typography>
                  <Typography variant="body-small">
                    {kbFileDetails?.source === "deskday"
                      ? "DeskDay"
                      : "File import"}
                  </Typography>
                </Stack>

                <Stack direction={"row"} alignItems={"center"} gap="16px">
                  <Typography
                    variant="body-small"
                    color={theme.palette.lightBg.low}
                    width={"90px"}
                  >
                    Created on
                  </Typography>
                  <Typography variant="body-small">{formattedDate}</Typography>
                </Stack>
                {mode === "view" && (
                  <Stack direction={"row"} alignItems={"center"} gap="16px">
                    <Typography
                      variant="body-small"
                      color={theme.palette.lightBg.low}
                      width={"90px"}
                    >
                      Viewing version
                    </Typography>
                    <Typography variant="body-small">
                      {kbFileDetails?.version === 0
                        ? "Draft"
                        : `Version ${kbFileDetails?.version}`}
                    </Typography>
                  </Stack>
                )}

                {hasPublishedVersions && (
                  <Stack direction={"row"} alignItems={"center"} gap="16px">
                    <Typography
                      variant="body-small"
                      color={theme.palette.lightBg.low}
                      width={"90px"}
                    >
                      Last modified
                    </Typography>
                    <Typography variant="body-small">
                      {formattedDate}
                    </Typography>
                  </Stack>
                )}

                <Stack direction={"row"} alignItems={"center"} gap="16px">
                  <Typography
                    variant="body-small"
                    color={theme.palette.lightBg.low}
                    width={"90px"}
                  >
                    Created by
                  </Typography>
                  <Stack direction={"row"} alignItems={"center"} gap={"8px"}>
                    <Avatar
                      url={kbFileDetails?.owner_profile_image}
                      fullname={fullName}
                      variants="sm"
                    />
                    <Typography variant="body-small">{fullName}</Typography>
                  </Stack>
                </Stack>
                {hasPublishedVersions && (
                  <Stack direction={"row"} alignItems={"center"} gap="16px">
                    <Typography
                      variant="body-small"
                      color={theme.palette.lightBg.low}
                      width={"88px"}
                    >
                      Last modified by
                    </Typography>
                    <Stack direction={"row"} alignItems={"center"} gap={"8px"}>
                      <Avatar
                        url={kbFileDetails?.updated_by_profile_image}
                        fullname={updatedByName}
                        variants="sm"
                      />
                      <Typography variant="body-small">
                        {updatedByName}
                      </Typography>
                    </Stack>
                  </Stack>
                )}
              </Stack>

              <Stack sx={{ gap: "8px" }}>
                <Typography variant="subtitle1">Save to</Typography>
                <Stack
                  onClick={() => {
                    if (haveFullAccess) {
                      setOpen(true);
                    }
                  }}
                  direction={"row"}
                  alignItems={"center"}
                  component={"button"}
                  type="button"
                  sx={{
                    height: "32px",
                    padding: "4px 12px",
                    border: `1px solid ${theme.palette.neutral["015"]}`,
                    borderRadius: "4px",
                    justifyContent: "space-between",
                    cursor:
                      mode === "edit" && haveFullAccess ? "pointer" : "inherit",
                    bgcolor: "#FFF",
                  }}
                >
                  <Typography
                    variant="body-medium"
                    color={theme.palette.lightBg.low}
                    noWrap
                  >
                    {parentName || "Select location"}
                  </Typography>
                  <DownArrowIcon
                    fontSize="small"
                    htmlColor={theme.palette.lightBg.low}
                  />
                </Stack>
              </Stack>
              {/* {mode === "view" &&
                kbFileDetails?.record_visibility === "public" &&
                kbFileDetails?.is_live && (
                  <KbShareLink articleId={kbFileDetails?.article_id} />
                )} */}
            </Stack>
          ) : (
            <Stack
              sx={{
                p: "16px 24px",
                gap: "8px",
              }}
            >
              {hasUnsavedVersion && (
                <VersionCard
                  key="unsaved"
                  versions={{ ...snapshot, ...draftVersion, isDraft: true }}
                />
              )}
              {kbFileDetails?.versions?.map((version) => {
                if (
                  version.version === 0 ||
                  version.record_visibility === "unsaved"
                )
                  return null;

                return (
                  <VersionCard
                    key={version.mapping_id}
                    versions={version}
                    // isSelected={selectedVersion === version.mapping_id}
                    // onClick={() => setSelectedVersion(version.mapping_id)}
                  />
                );
              })}
            </Stack>
          )}
        </>
      )}

      {open && (
        <KbFileListDrawer
          title="Select folder"
          primaryButtonText="Save"
          onPrimaryButtonClick={(item) => {
            const folderId = item[0];
            setValue("parentId", folderId);

            const folder = findFolderById(kbFolderTreeAtomData, folderId);
            if (folder) {
              setParentName(folder.name);
            }
            handleMoveRecord(folderId);
            setOpen(false);
          }}
          onSecondaryButtonClick={() => {
            // setValue("parentId", null);
            setOpen(false);
          }}
          onClose={() => {
            setOpen(false);
          }}
          open={open}
          selectedValues={
            kbFileDetails?.parent_id ? [kbFileDetails?.parent_id] : []
          }
        />
      )}
    </>
  );
};

const VersionCard = ({
  versions,
}: {
  versions?: KbFileDetailResponse & { isDraft?: boolean };
}) => {
  const { fileId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const fullname = getFullName(
    versions?.owner_first_name,
    "",
    versions?.owner_last_name,
  );

  const isSelected =
    searchParams.get("v_id") === versions?.mapping_id ||
    (!searchParams.get("v_id") && versions?.is_live);

  const onClick = () => {
    if (versions?.mapping_id) {
      setSearchParams({
        ...(versions.is_live ? {} : { v_id: versions?.mapping_id }),
      });
      // navigate(`/app/kb/document/${versions?.mapping_id}`);
    }
  };
  return (
    <Stack
      direction={"row"}
      sx={{
        p: "8px 12px",
        border: isSelected
          ? `1px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.neutral["015"]}`,
        borderRadius: "4px",
        justifyContent: "space-between",
        cursor: "pointer",
        backgroundColor: isSelected
          ? theme.palette.secondary["purple-tint-3"]
          : "transparent",
      }}
      onClick={onClick}
    >
      <Stack direction={"row"} gap="8px">
        <Avatar
          sx={{ width: "32px", height: "32px" }}
          fullname={fullname}
          src={versions?.owner_profile_image}
        >
          V1
        </Avatar>
        <Stack sx={{ gap: "4px" }}>
          <Typography variant="body-medium">
            {versions?.isDraft ? "Draft" : `Version ${versions?.version}`}
          </Typography>
          <Stack
            direction={"row"}
            sx={{
              gap: "8px",
              alignItems: "center",
            }}
          >
            {versions && versions.is_live && (
              <Stack
                direction={"row"}
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  height: "24px",
                  borderRadius: "4px",
                  gap: "8px",
                  backgroundColor: theme.palette.primitive.system.violet[50],
                  p: "4px 8px",
                }}
              >
                <Box
                  sx={{
                    height: "4px",
                    width: "4px",
                    borderRadius: "50%",
                    backgroundColor: theme.palette.primitive.system.violet[600],
                  }}
                ></Box>
                <Typography
                  variant="tag-label-xs"
                  sx={{
                    color: theme.palette.primitive.system.violet[600],
                  }}
                >
                  Live
                </Typography>
              </Stack>
            )}

            <Typography variant="body-small" color={theme.palette.lightBg.low}>
              by {fullname}
              {/* {claculateTimeElapsed(Date.parse(versions?.created_at || ""))} ago */}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Typography variant="body-small" color={theme.palette.lightBg.low}>
        {claculateTimeElapsed(Date.parse(versions?.created_at || ""))} ago
      </Typography>
    </Stack>
  );
};
